import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { first, firstValueFrom, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { Checklist } from '../interface/checklist';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { DataService } from './data.service';
import { Item } from '../interface/item';
import { RoLogsService } from './ro-logs.service';
import { RO_LOG_ACTION_TYPES } from '@app/interface/os-logs';
import { MkgOS } from '@app/class/mkg-os';
import { CheckListItemStorageConfig, FbImageDownloaderService, FIREBASE_STORAGE_PATHS } from './fb-image-downloader.service';

@Injectable({
  providedIn: 'root'
})
export class ChecklistService {

  /**
   * To get, create, update or delete the checklist, we should to use the CNPJ of matrix company
   * 
   * To add, update or remove a checklist from the OS, we should to use the CNPJ where the OS is in (logged-in company)
   */
  private _isFromMatrix = true;

  private itemUpdatedSubject = new Subject<Item>();
  public itemUpdated$ = this.itemUpdatedSubject.asObservable();
  private _fbImageDownloaderService = inject(FbImageDownloaderService);

  constructor(
    private _http: HttpClient,
    private dataService: DataService,
    private _storage: AngularFireStorage,
    private _roLogService: RoLogsService
  ) { }

  public async getAll() {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/checklist`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp: { checklist: Checklist[] } = await this._http.get(url, options).pipe(first()).toPromise() as any;
    return resp.checklist;
  }

  public async get(id: string) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/checklist/${id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this._http.get(url, options).pipe(first()).toPromise();
    return resp;
  }

  public async addOrUpdate(checklist: Checklist) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/checklist`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    if (!checklist.favorite) {
      (checklist as any).favorite = '0';
    }
    const resp = await this._http.post(url, checklist, options).pipe(first()).toPromise();
    return resp;
  }

  public async delete(id: string) {
    const url = `${environment.mkgoURL}/api/v1/companies-actions/checklist/${id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this._isFromMatrix));
    const resp = await this._http.delete(url, options).pipe(first()).toPromise();
    return resp;
  }

  public async addChecklistToOS(OS: MkgOS, checklist: Checklist) {
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/checklist`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    // (checklist as any)['lastUpdate'] = new Date().toISOString();
    checklist['template'] = checklist.id;
    delete checklist['id']
    checklist['favorite'] = String(checklist.favorite);
    
    try {
      const resp = await firstValueFrom(this._http.post(url, checklist, options).pipe(first()));
      
      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.ADD_COMMON_CHECKLIST_TO_OS,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
      })
      
      return resp;
    } catch (error) {
      console.error(error);
      throw error;
    }
    
  }

  public async addChecklistItemToOS(OS: MkgOS, checklist: Checklist, item: Item) {
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/checklist/${checklist.id}/item/${item.id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));

    try {
      const itemWithLastUpdate = {
        ...item,
        lastUpdate: true
      };

      const resp: any = await firstValueFrom(this._http.post(url, itemWithLastUpdate, options));

      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.UPDATE_COMMON_CHECKLIST_ITEM_TO_OS,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
      })


      if (resp && resp.changeHistory) {
        Object.assign(item, resp);
        this.itemUpdatedSubject.next(item);
      }

      return resp;
    } catch (error) {
      console.error(error);
      throw error;
    }
  } 
  
  public async updateChecklist(OS: MkgOS, checklist: Checklist){
    const url = `${environment.mkgoURL}/api/v1/os/${OS.id}/checklist/${checklist.id}`;
    const options = await firstValueFrom(this.dataService.httpOptions(false));
    
    try {
      const resp = await firstValueFrom(this._http.put(url,checklist,options));

      await this._roLogService.create({
        actionType: RO_LOG_ACTION_TYPES.UPDATE_COMMON_CHECKLIST_INTO_OS,
        osId: OS.id,
        statusInTheLogRecord: OS.currentState,
      })
      
      return resp;
    } catch (error) {
      console.error(error);
      throw error;
    }
    
  }

  async saveItemFile(config: CheckListItemStorageConfig, data: Blob): Promise<string> {
    const path = FIREBASE_STORAGE_PATHS.checklist(config);
    await this._storage.upload(path, data);
    const id = await this._storage.ref(path).getDownloadURL().pipe(first()).toPromise();
    return id
  }

  async deleteItemFile(config: CheckListItemStorageConfig): Promise<void> {
    const path = FIREBASE_STORAGE_PATHS.checklist(config);
    await this._storage.ref(path).delete().pipe(first()).toPromise();
  }

  async getBlob(config: CheckListItemStorageConfig): Promise<Blob> {
    const path = FIREBASE_STORAGE_PATHS.checklist(config);
    return firstValueFrom(this._fbImageDownloaderService.getBlob({ path }));
  }

}


